import { useDispatch, useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../../../selectors/home'
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
    alpha,
    Checkbox,
    FormControl,
    InputAdornment,
    Link,
    List,
    Menu,
    MenuItem,
    Snackbar,
    TextField,
    Typography
} from '@mui/material'
import { SearchParamsContext } from '../../../../components/providers/searchParamsProvider'
import { selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import { getSecurityKey, isFnOSecurity } from '../../../../util/security'
import {
    CallPutType,
    MultiStrikeSecuritiesType,
    setMultiStrikeNearAtm,
    setMultiStrikeSecurities
} from '../../../../reducer/multiStrikeChart.reducer'
import {
    selectMultiStrikeAtm,
    selectMultiStrikeChartSecurities,
    selectMultiStrikeChartStyles,
    selectMultiStrikeLoading,
    selectMultiStrikeNearAtm
} from '../../../../selectors/multiStrikeChart'
import { AppContext } from '../../../../App'
import { ExtraSmallButton } from '../../../../components/custom'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { SearchOutlined } from '@mui/icons-material'
import { updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import { ChartViewKey } from '../../../../assets/constants'
import {
    selectMultiStrikeChartConfigAtm,
    selectMultiStrikeChartConfigExpiry,
    selectMultiStrikeChartConfigNearAtm,
    selectMultiStrikeChartConfigSecurities,
    selectMultiStrikeChartConfigStyles
} from '../../../../selectors/chart'
import { DropDownStrike } from '../../../../reducer/options.reducer'
import { findNearestNumber } from '../../../../util/tradepanel'
import { selectSnapshot } from '../../../../selectors/security'
import { selectIsEmbedded } from '../../../../selectors/device'

export const getStrikeAndStrikeData = (atm: DropDownStrike, priceMultiplier: number, isDecimal: boolean) => {
    const atmStrike = (atm.strike / priceMultiplier)
    const strike = isDecimal ? atmStrike.toFixed(2) : atmStrike.toString()
    const strikeData = {
        callSecKey: getSecurityKey(atm.callSecurity.exchange, atm.callSecurity.code),
        putSecKey: getSecurityKey(atm.putSecurity.exchange, atm.putSecurity.code),
        callPut: ['CE', 'PE'] as CallPutType[]
    }
    return { strike, strikeData }
}

const MultiStrikesMenu = ({ viewKey }: { viewKey: string }) => {
    const dispatch = useDispatch()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const { searchParams } = useContext(SearchParamsContext)!
    const appContext = useContext(AppContext)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const chart = useMemo(() => appContext.getMultiStrikeChart(viewKey), [appContext.multiStrikeChart[viewKey], viewKey])
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const urlexpiry = decodeURIComponent(searchParams.get('expiry') || '')
    const stateExpiry = useSelector(selectMultiStrikeChartConfigExpiry(viewKey))
    const expiry = useMemo(() => isWidget ? stateExpiry : urlexpiry, [isWidget, stateExpiry, urlexpiry])
    const { primary, underlying } = useSelector(selectPrimarySecurity)
    const mainSecurity = primary && isFnOSecurity(primary) ? underlying : primary
    const underlyingToken = getSecurityKey(mainSecurity?.exchange ?? 0, mainSecurity?.code ?? '')
    const underlyingSnaphot = useSelector(selectSnapshot(underlyingToken))
    const optionsExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(underlyingToken))
    const nearAtm = useSelector(isWidget ? selectMultiStrikeChartConfigNearAtm(viewKey) : selectMultiStrikeNearAtm)
    const atmSelected = useSelector(isWidget ? selectMultiStrikeChartConfigAtm(viewKey) : selectMultiStrikeAtm)
    const {
        autoRefresh,
        notifyInterval
    } = useSelector(isWidget ? selectMultiStrikeChartConfigStyles(viewKey) : selectMultiStrikeChartStyles)
    const [searchText, setSearchText] = useState('')
    const listRefs = useRef<(HTMLLIElement | null)[]>([])
    const scrolledToSelected = useRef(false)
    const loading = useSelector(selectMultiStrikeLoading)
    const [roundingStrikesRequired, setRoundingStrikesRequired] = useState(false)
    const [lastTriggerTime, setLastTriggerTime] = useState<number | null>(null)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [currentAtmStrike, setCurrentAtmStrike] = useState<number | undefined>(atmSelected)
    const isEmbedded = useSelector(selectIsEmbedded)

    useEffect(() => {
        setRoundingStrikesRequired(false)
    }, [underlyingToken])

    useEffect(() => {
        const refreshStrike = () => {
            if (nearAtm) {
                handleAtmStrikeClick(nearAtm)
            }
        }
        if (autoRefresh) {
            const intervalId = setInterval(() => {
                refreshStrike()
            }, 30000)
            return () => clearInterval(intervalId)
        }
        return undefined
    }, [autoRefresh])

    const multiStrikeChartSecurities = useSelector(isWidget ? selectMultiStrikeChartConfigSecurities(viewKey) : selectMultiStrikeChartSecurities)
    const multiStrikeChartSecuritiesKeys = useMemo(() => Object.keys(multiStrikeChartSecurities ?? {}), [multiStrikeChartSecurities])

    const calculateAtmStrike = useMemo(() => {
        if (chart && optionsExpiries) {
            const expiryIndex = optionsExpiries.findIndex(o => o.expiry.name === expiry)
            if (expiryIndex !== undefined && expiryIndex >= 0) {
                const expiryData = optionsExpiries[expiryIndex]
                if (expiryData && underlyingSnaphot) {
                    return findNearestNumber(expiryData.strikesArray, underlyingSnaphot.lastTradedPrice) // Return the current ATM strike
                }
            }
        }
        return null
    }, [chart, optionsExpiries, expiry, underlyingSnaphot])

    useEffect(() => {
        const checkAndNotify = () => {
            const currentTime = Date.now()
            const currentAtmStrike = calculateAtmStrike
            if (
                currentAtmStrike !== null &&
                currentAtmStrike !== atmSelected &&
                (lastTriggerTime === null || currentTime - lastTriggerTime >= notifyInterval * 1000)
            ) {
                setCurrentAtmStrike(currentAtmStrike)
                setLastTriggerTime(currentTime)
                if (!isNaN(atmSelected ?? NaN) && !isNaN(currentAtmStrike ?? NaN)) {
                    setOpenSnackbar(true)
                }
            }
        }

        if (!autoRefresh) {
            const intervalId = setInterval(() => {
                checkAndNotify()
            }, 5000)

            return () => clearInterval(intervalId)
        }
    }, [autoRefresh, notifyInterval, calculateAtmStrike, lastTriggerTime])

    const ivStrikesMenu = useMemo(() => {
        const expiryIndex = optionsExpiries?.findIndex(o => o.expiry.name === expiry)
        if (expiryIndex !== undefined && expiryIndex >= 0) {
            const expiryData = optionsExpiries?.[expiryIndex]
            if (expiryData) {
                const data = expiryData.strikes.map((s) => {
                    const price = (s.strike) / (mainSecurity?.priceMultiplier ?? 100)
                    if (!Number.isInteger(price) && !roundingStrikesRequired) setRoundingStrikesRequired(true)
                    return {
                        label: !roundingStrikesRequired ? price.toString() : price.toFixed(2),
                        callValue: getSecurityKey(s.callSecurity.exchange ?? 0, s.callSecurity?.code ?? ''),
                        putValue: getSecurityKey(s.putSecurity?.exchange ?? 0, s.putSecurity?.code ?? '')
                    }
                })
                return !searchText ? data : data.filter(d => d.label.includes(searchText))
            }
        }
        return []
    }, [expiry, mainSecurity, optionsExpiries, searchText, roundingStrikesRequired])

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        AnalyticsManager.logEvent('Strike Button Clicked', { source: 'multistrike' })
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const scrollSelectedInView = useCallback((multiStrikeChartSecuritiesPassed = multiStrikeChartSecurities) => {
        if (anchorEl) {
            const minselectedValue = Math.min(...Object.keys(multiStrikeChartSecuritiesPassed ?? {}).map(v => Number(v)))
            const index = ivStrikesMenu.findIndex(i => i.label === (roundingStrikesRequired ? minselectedValue.toFixed(2) : minselectedValue.toString()))
            setTimeout(() => {
                if (index >= 0 && listRefs.current[index] && !scrolledToSelected.current) {
                    listRefs.current[index]?.scrollIntoView({
                        block: 'start'
                    })
                    scrolledToSelected.current = true
                }
            }, 100)
        } else {
            scrolledToSelected.current = false
        }
    }, [anchorEl, ivStrikesMenu, multiStrikeChartSecurities, roundingStrikesRequired])

    useEffect(() => {
        scrollSelectedInView()
    }, [scrollSelectedInView])

    const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = e.target
        if (/^\d+$/.test(value)) {
            setSearchText(value)
        } else if (!value) {
            setSearchText('')
        }
    }

    const handleSnackbarClose = useCallback(() => {
        setOpenSnackbar(false)
    }, [])

    const handleChangeClick = useCallback(() => {
        if (nearAtm) {
            handleAtmStrikeClick(nearAtm)
        }
        setOpenSnackbar(false)
    }, [nearAtm])

    const addOrRemoveSecurity = useCallback((strike: string, strikeData: {
        callSecKey: string,
        putSecKey: string,
        callPut: CallPutType[]
    }) => {
        const data = { ...multiStrikeChartSecurities }

        if (data[strike]) {
            delete data[strike]
            if (chart) chart.dataManager.removeStrike(strike)
            AnalyticsManager.logEvent('Strike Removed', { strike: strike, source: 'multistrike' })
        } else {
            if (multiStrikeChartSecuritiesKeys.length < 10) {
                data[strike] = strikeData.callPut
                if (chart) chart.dataManager.addNewSecurities(strike, strikeData)
                AnalyticsManager.logEvent('Strike Added', { strike: strike, source: 'multistrike' })
            }
        }
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({ viewKey, config: { securities: data } }))
        } else {
            dispatch(setMultiStrikeSecurities(data))
        }
        setTimeout(() => {
            chart?.onCrosshairMove({} as any)
        }, 500)
    }, [multiStrikeChartSecurities, isWidget, chart, multiStrikeChartSecuritiesKeys.length, dispatch, viewKey])

    const handleAtmStrikeClick = useCallback(async (i: number) => {
        AnalyticsManager.logEvent('Near ATM Strike Selected', { numberOfStrike: i, source: 'stockbar' })
        if (chart) {
            const expiryIndex = optionsExpiries?.findIndex(o => o.expiry.name === expiry)
            if (expiryIndex !== undefined && expiryIndex >= 0) {
                const expiryData = optionsExpiries?.[expiryIndex]
                if (expiryData) {
                    const atmStrike = findNearestNumber(expiryData.strikesArray, underlyingSnaphot?.lastTradedPrice)
                    const atm = expiryData.strikes.find(s => s.strike === atmStrike)
                    const atmIndex = expiryData.strikes.findIndex(s => s.strike === atmStrike)
                    if (atm) {
                        if (atmSelected === atm.strike && i === nearAtm) return
                        if (isWidget) {
                            dispatch(updateMultiStrikeChartConfig({
                                viewKey, config: {
                                    nearAtm: i,
                                    atm: atm.strike
                                }
                            }))
                        } else {
                            dispatch(setMultiStrikeNearAtm({
                                nearAtm: i,
                                atm: atm.strike
                            }))
                        }
                        const {
                            strike,
                            strikeData
                        } = getStrikeAndStrikeData(atm, mainSecurity?.priceMultiplier ?? 100, roundingStrikesRequired)
                        let data = {
                            [strike]: strikeData
                        }
                        const a = [3, 5, 7, 9]
                        a.forEach((x) => {
                            if (i >= x) {
                                const index = x === 3 ? 1 : x === 5 ? 2 : x === 7 ? 3 : 4
                                const atm1 = expiryData.strikes[atmIndex - index]
                                const atm2 = expiryData.strikes[atmIndex + index]
                                const {
                                    strike: strike1,
                                    strikeData: strikeData1
                                } = getStrikeAndStrikeData(atm1, mainSecurity?.priceMultiplier ?? 100, roundingStrikesRequired)
                                const {
                                    strike: strike2,
                                    strikeData: strikeData2
                                } = getStrikeAndStrikeData(atm2, mainSecurity?.priceMultiplier ?? 100, roundingStrikesRequired)
                                data = {
                                    [strike1]: strikeData1,
                                    ...data,
                                    [strike2]: strikeData2
                                }
                            }
                        })
                        chart.dataManager.removeAllSecondarySecurities()
                        const formattedData: MultiStrikeSecuritiesType = Object.fromEntries(
                            Object.entries(data).map(([key, value]) => [key, value.callPut])
                        );
                        if (isWidget) {
                            dispatch(updateMultiStrikeChartConfig({ viewKey, config: {
                                nearAtm: i,
                                securities: formattedData 
                            }}))
                        } else {
                            dispatch(setMultiStrikeSecurities(formattedData))
                        }
                        await chart.dataManager.addNewStrikes(data)
                        chart.onCrosshairMove({} as any)
                        chart.resetTimeScale()
                        scrolledToSelected.current = false
                        scrollSelectedInView(formattedData)
                    }
                }
            }
        }
    }, [chart, optionsExpiries, expiry, underlyingSnaphot?.lastTradedPrice, atmSelected, nearAtm, isWidget, mainSecurity?.priceMultiplier, roundingStrikesRequired, scrollSelectedInView, dispatch, viewKey])

    return (
        <>
            <FormControl sx={{ minWidth: '70px', height: '32px' }}>
                <ExtraSmallButton
                    color="inherit"
                    id="multistrike-strike-menu-button"
                    variant="outlined"
                    aria-controls={open ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    sx={{
                        height: '100%',
                        textWrap: 'nowrap',
                        borderColor: theme => theme.palette.background.default
                    }}
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                >
                    {nearAtm ? `Near ATM ${nearAtm}` : `${multiStrikeChartSecuritiesKeys.length || ''} Strike${multiStrikeChartSecuritiesKeys.length > 1 ? 's' : ''}`}
                </ExtraSmallButton>
                <Menu
                    key={'multistrike-menu'}
                    id="demo-multiple-checkbox"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    sx={{
                        backgroundColor: 'transparent',
                        '.MuiMenu-list ': { display: 'flex !important', flexDirection: 'column' }
                    }}
                    MenuListProps={{
                        sx: {
                            maxHeight: '60vh'
                        }
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                >
                    <Typography variant={'caption'} color="textSecondary" sx={{ p: '6px 16px', display: 'block' }}>NEAR
                        ATM <span style={{ fontSize: '10px' }}>(Refreshes in 1M)</span></Typography>
                    <div id={'default-strikes'} style={{ display: 'flex' }}>
                        {
                            [1, 3, 5, 7, 9].map((i) => {
                                return (
                                    <ExtraSmallButton
                                        key={i}
                                        onClick={() => handleAtmStrikeClick(i)}
                                        sx={{
                                            borderColor: i === nearAtm ? theme => theme.palette.primary.main : 'default',
                                            background: i === nearAtm ? theme => alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity) : 'inherit',
                                            color: i !== nearAtm ? theme => theme.palette.text.secondary : theme => theme.palette.primary.main,
                                            width: '40px',
                                            height: '40px'
                                        }}
                                        variant="outlined"
                                    >
                                        {i}
                                    </ExtraSmallButton>
                                )
                            })
                        }
                    </div>
                    <Typography variant={'caption'} color="textSecondary" sx={{ p: '6px 16px', display: 'block' }}>FIXED
                        STRIKES</Typography>
                    <TextField
                        value={searchText}
                        onChange={handleSearchTextChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ ml: '18px' }}>
                                    <SearchOutlined />
                                </InputAdornment>
                            )
                        }}
                        onClick={() => {
                            if (isWidget) {
                                dispatch(updateMultiStrikeChartConfig({
                                    viewKey, config: {
                                        nearAtm: undefined,
                                        atm: undefined
                                    }
                                }))
                            } else {
                                dispatch(setMultiStrikeNearAtm({
                                    nearAtm: undefined,
                                    atm: undefined
                                }))
                            }
                        }}
                        sx={{
                            width: '200px'
                        }}
                        variant="standard"
                    />
                    <List sx={{ overflowY: 'scroll' }} onClick={() => {
                        if (isWidget) {
                            dispatch(updateMultiStrikeChartConfig({
                                viewKey, config: {
                                    nearAtm: undefined,
                                    atm: undefined
                                }
                            }))
                        } else {
                            dispatch(setMultiStrikeNearAtm({
                                nearAtm: undefined,
                                atm: undefined
                            }))
                        }
                    }}>
                        {
                            ivStrikesMenu.length > 0 &&
                            ivStrikesMenu?.map((o: any, i: number) => {
                                const selected = !!multiStrikeChartSecurities?.[o.label]
                                return (
                                    <MenuItem key={o.label} ref={(el) => {
                                        listRefs.current[i] = el
                                    }} disabled={!!nearAtm || loading} onClick={() => {
                                        if (!loading) addOrRemoveSecurity(o.label, {
                                            callSecKey: o.callValue,
                                            putSecKey: o.putValue,
                                            callPut: ['CE', 'PE'] as CallPutType[]
                                        })
                                    }} selected={selected} sx={{ height: '40px', padding: '6px 10px' }}>
                                        <Checkbox sx={{ '&:hover': { bgcolor: 'transparent' } }} checked={selected} />
                                        <Typography sx={{ marginRight: '10px' }}>
                                            {o.label}
                                        </Typography>
                                    </MenuItem>
                                )
                            })}
                    </List>
                </Menu>
            </FormControl>
            {!isEmbedded && <Snackbar
                open={openSnackbar}
                onClose={handleSnackbarClose}
                message={
                    <>
                        {`ATM changed from ${atmSelected! / (mainSecurity?.priceMultiplier ?? 100)} to ${currentAtmStrike! / (mainSecurity?.priceMultiplier ?? 100)}.`}
                        <br />
                        <Link
                            component="button"
                            variant="body2"
                            color="primary"
                            onClick={handleChangeClick}
                            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                        >
                            Click to update on the chart
                        </Link>
                    </>
                }
                autoHideDuration={10000}
            />}
        </>
    )
}


export default memo(MultiStrikesMenu)